import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Form,
  Input,
  Label,
  Row,
  Col,
  Table,
  Spinner,
  InputGroup,
  Card,
  CardBody,
  CardHeader,
} from "reactstrap";
import PopupModal from "../../components/Common/PopupModal";
import api from "../../api";

const RateListManagement = () => {
  const [popup, setPopup] = useState({
    isOpen: false,
    message: "",
    backgroundColor: "#1d222e",
  });
  
  // RateList state
  const [rateListEntries, setRateListEntries] = useState([]);
  const [rateListLoading, setRateListLoading] = useState(false);
  const [newPokeName, setNewPokeName] = useState("");
  const [newPokePrice, setNewPokePrice] = useState("");
  const [editingEntry, setEditingEntry] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const handlePopup = useCallback((message, backgroundColor = "#1d222e") => {
    setPopup({ isOpen: true, message, backgroundColor });

    setTimeout(() => {
      setPopup((prev) => ({ ...prev, isOpen: false }));
    }, 2000);
  }, []);
  
  const fetchRateList = useCallback(async () => {
    setRateListLoading(true);
    try {
      const res = await api.get("/v1/admin/ratelist", {
        withCredentials: true,
      });
      setRateListEntries(res.data.entries || []);
    } catch (err) {
      console.error("Failed to fetch ratelist:", err);
      handlePopup("Failed to fetch ratelist", "red");
    } finally {
      setRateListLoading(false);
    }
  }, [handlePopup]);
  
  const handleAddRateListEntry = async () => {
    if (!newPokeName || !newPokePrice) {
      handlePopup("Please enter both Pokémon name and price", "red");
      return;
    }
    
    try {
      const payload = {
        poke_name: newPokeName,
        price: newPokePrice
      };
      
      const res = await api.post("/v1/admin/ratelist", payload, {
        withCredentials: true,
      });
      
      handlePopup(res.data.message, "#1d222e");
      fetchRateList();
      
      setNewPokeName("");
      setNewPokePrice("");
    } catch (err) {
      handlePopup(err.response?.data.detail || "Failed to add ratelist entry", "red");
    }
  };
  
  const handleUpdateRateListEntry = async (entry) => {
    try {
      const payload = {
        poke_name: entry.poke_name,
        price: entry.price
      };
      
      const res = await api.put(`/v1/admin/ratelist/${entry.poke_name}`, payload, {
        withCredentials: true,
      });
      
      handlePopup(res.data.message, "#1d222e");
      setEditingEntry(null);
      fetchRateList();
    } catch (err) {
      handlePopup(err.response?.data.detail || "Failed to update ratelist entry", "red");
    }
  };
  
  const handleDeleteRateListEntry = async (pokeName) => {
    if (!window.confirm(`Are you sure you want to delete ${pokeName} from the ratelist?`)) {
      return;
    }
    
    try {
      const res = await api.delete(`/v1/admin/ratelist/${pokeName}`, {
        withCredentials: true,
      });
      
      handlePopup(res.data.message, "#1d222e");
      fetchRateList();
    } catch (err) {
      handlePopup(err.response?.data.detail || "Failed to delete ratelist entry", "red");
    }
  };
  
  const filteredRateListEntries = () => {
    if (!searchTerm) return rateListEntries;
    
    return rateListEntries.filter(entry => 
      entry.poke_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  useEffect(() => {
    fetchRateList();
  }, [fetchRateList]);

  return (
    <div className="page-content">
      <div className="container-fluid">
        <h4 className="mb-4">RateList Management</h4>
        
        <Card className="mb-4">
          <CardHeader>
            <h5 className="mb-0">Add New RateList Entry</h5>
          </CardHeader>
          <CardBody>
            <Form>
              <Row className="mb-3">
                <Col md="5">
                  <Label>Pokémon Name</Label>
                  <Input 
                    type="text" 
                    value={newPokeName} 
                    onChange={(e) => setNewPokeName(e.target.value)}
                    placeholder="Enter Pokémon name"
                  />
                </Col>
                <Col md="5">
                  <Label>Price</Label>
                  <InputGroup>
                    <Input 
                      type="text" 
                      value={newPokePrice} 
                      onChange={(e) => setNewPokePrice(e.target.value)}
                      placeholder="e.g. 100k, 1.5M"
                    />
                  </InputGroup>
                </Col>
                <Col md="2" className="d-flex align-items-end">
                  <Button 
                    color="primary" 
                    className="w-100" 
                    onClick={handleAddRateListEntry}
                    disabled={!newPokeName || !newPokePrice}
                  >
                    Add Entry
                  </Button>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
        
        <Card>
          <CardHeader>
            <Row>
              <Col md="6">
                <h5 className="mb-0">RateList Entries ({rateListEntries.length})</h5>
              </Col>
              <Col md="6">
                <Input 
                  type="text" 
                  placeholder="Search by Pokémon name" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            {rateListLoading ? (
              <div className="text-center my-5">
                <Spinner color="primary" />
              </div>
            ) : (
              <Table striped responsive>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Pokémon Name</th>
                    <th>Price</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRateListEntries().map((entry, index) => (
                    <tr key={entry.poke_name}>
                      <td>{index + 1}</td>
                      <td>
                        {editingEntry === entry.poke_name ? (
                          <Input 
                            type="text" 
                            value={entry.poke_name} 
                            disabled 
                          />
                        ) : (
                          entry.poke_name
                        )}
                      </td>
                      <td>
                        {editingEntry === entry.poke_name ? (
                          <Input 
                            type="text" 
                            value={entry.price} 
                            onChange={(e) => {
                              const updatedEntries = [...rateListEntries];
                              const entryIndex = updatedEntries.findIndex(e => e.poke_name === entry.poke_name);
                              updatedEntries[entryIndex].price = e.target.value;
                              setRateListEntries(updatedEntries);
                            }} 
                          />
                        ) : (
                          entry.price
                        )}
                      </td>
                      <td>
                        {editingEntry === entry.poke_name ? (
                          <>
                            <Button 
                              color="success" 
                              size="sm" 
                              className="me-2" 
                              onClick={() => handleUpdateRateListEntry(entry)}
                            >
                              Save
                            </Button>
                            <Button 
                              color="secondary" 
                              size="sm" 
                              onClick={() => {
                                setEditingEntry(null);
                                fetchRateList();
                              }}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button 
                              color="info" 
                              size="sm" 
                              className="me-2" 
                              onClick={() => setEditingEntry(entry.poke_name)}
                            >
                              Edit
                            </Button>
                            <Button 
                              color="danger" 
                              size="sm" 
                              onClick={() => handleDeleteRateListEntry(entry.poke_name)}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredRateListEntries().length === 0 && (
                    <tr>
                      <td colSpan="4" className="text-center">
                        {searchTerm ? "No matching entries found" : "No entries found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </div>
      
      <PopupModal
        isOpen={popup.isOpen}
        message={popup.message}
        backgroundColor={popup.backgroundColor}
        onClose={() => setPopup({ ...popup, isOpen: false })}
      />
    </div>
  );
};

export default RateListManagement;
